/**
 * Fact Preview Service
 *
 * Provides helper functions for normalizing fact highlight strings,
 * deduplicating them, and determining whether a fact should be treated
 * as tabular based on its metadata.
 */

// Type definitions
export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PositionData {
  pageIndex: number;
  boundingBox?: BoundingBox | null;
}

export interface FactData {
  highlightContentString?: string;
  factSourceType?: 'text' | 'image' | 'tabular';
  factFromImage?: boolean;
  content?: string;
  text?: string;
  pageIndex?: number;
  pageIndices?: number[];  // All pages where fact appears (for multi-page chunks)
  positions?: PositionData[];  // All position objects with pageIndex and boundingBox
  boundingBox?: BoundingBox;
  contextType?: string;
}

export interface DocumentData {
  docId?: string;
  documentId?: string; // Alias for docId used in some contexts
  originalFilename?: string;
  filePath?: string;
  documentCategory?: string;
  projectId?: string;
  dealId?: string;
  documentType?: string;
  documentOrigin?: string;
  documentSource?: string;
  updatedAt?: string;
  lastUpdatedBy?: string;
  isUserUploaded?: boolean;
  sharepointSiteId?: string;
  sharepointDriveId?: string;
  sharepointItemId?: string;
  title?: string;
  documentTitle?: string;
  documentName?: string;
}

// FactHighlightSource — used for building highlight requests.
// Document metadata lives on the registry document, NOT here.
export interface FactHighlightSource {
  id?: string;
  documentId?: string;
  docId?: string;
  previewHtml?: string;
  table?: boolean;
  facts?: FactData[];
  document?: DocumentData;
  docDisplayLabel?: string;  // Backend stores the document filename on every fact
  [key: string]: unknown;   // Allow additional properties from fact objects
}

export interface HighlightEntry {
  originalText: string;
  normalizedText: string;
  pageIndex: number | null;
  pageIndices?: number[] | null;  // All pages where this highlight appears
  boundingBox: BoundingBox | null;
  factIndex: number;
}

export interface HighlightRequest {
  documentId: string;
  highlightTexts: string[];
  highlightPageIndices: (number | null)[];
  highlightAllPageIndices?: ((number[] | null) | null)[];  // All pages per highlight (for multi-page chunks)
  preferredFactIndex: number | null;
  isTabular: boolean;
  containsImageFacts: boolean;
  preferredFactSourceType: 'text' | 'image' | null;
  factIdToIndex?: Record<string, number>; // Map of factId to chat display index (per-message numbering)
  [key: string]: unknown; // Allow additional properties
}

export interface FactHighlight {
  id: string;
  text: string;
  pageIndex: number | null;
  pageIndices?: number[] | null;  // All pages where fact appears (for multi-page chunks)
}

/**
 * Normalize a single highlight text string
 * - Trim whitespace
 * - Collapse multiple newlines into single spaces
 * - Collapse multiple spaces into single spaces
 */
export function normalizeHighlightText(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  return text
    .trim()
    .replace(/\n+/g, ' ')  // Replace newlines with spaces
    .replace(/\s+/g, ' ')   // Collapse multiple spaces
    .trim();
}

/**
 * Normalize an array of highlight text strings and remove duplicates
 */
export function normalizeAndDeduplicateHighlights(texts: string[]): string[] {
  if (!Array.isArray(texts)) {
    return [];
  }

  const normalized: string[] = texts
    .map((text: string) => normalizeHighlightText(text))
    .filter((text: string) => text.length > 0);

  // Remove duplicates while preserving order
  return [...new Set(normalized)];
}

/**
 * Determine if a fact should be treated as tabular
 */
export function isTabularFact(factSource: FactHighlightSource): boolean {
  if (!factSource) {
    return false;
  }

  if (factSource.previewHtml && (factSource.previewHtml as string).trim().length > 0) {
    return true;
  }

  if (factSource.table === true) {
    return true;
  }

  if (Array.isArray(factSource.facts)) {
    return factSource.facts.some((fact: FactData) => fact.contextType === 'table');
  }

  return false;
}

/**
 * Build a highlight request object from a fact source.
 */
export function buildHighlightRequest(
  factSource: FactHighlightSource,
  preferredFactIndex: number | null = null
): HighlightRequest | null {
  if (!factSource) {
    return null;
  }

  const documentId = factSource.documentId || factSource.docId || '';
  if (!documentId) {
    return null;
  }

  // Extract highlight texts from facts
  const highlightEntries: HighlightEntry[] = [];
  let containsImageFacts: boolean = false;
  let preferredFactSourceType: 'text' | 'image' | null = null;

  if (Array.isArray(factSource.facts)) {
    factSource.facts.forEach((fact: FactData, factIndex: number) => {
      const factSourceType: 'text' | 'image' =
        (fact.factSourceType === 'tabular' ? 'text' : fact.factSourceType) ||
        (fact.factFromImage ? 'image' : 'text');

      if (factSourceType === 'image') {
        containsImageFacts = true;
        if (preferredFactIndex === factIndex) {
          preferredFactSourceType = 'image';
        }
      } else if (preferredFactIndex === factIndex) {
        preferredFactSourceType = 'text';
      }

      const highlightContent: string =
        fact.highlightContentString ||
        fact.content ||
        fact.text ||
        '';

      if (highlightContent && highlightContent.trim().length > 0) {
        let pageIndex: number | null = null;
        if (typeof fact.pageIndex === 'number' && Number.isFinite(fact.pageIndex)) {
          pageIndex = fact.pageIndex;
        }

        let pageIndices: number[] | null = null;
        if (Array.isArray(fact.pageIndices) && fact.pageIndices.length > 0) {
          pageIndices = fact.pageIndices.filter(
            (p): p is number => typeof p === 'number' && Number.isFinite(p)
          );
          if (pageIndices.length === 0) pageIndices = null;
        }

        const boundingBox: BoundingBox | null = fact.boundingBox || null;
        const normalized = normalizeHighlightText(highlightContent);

        highlightEntries.push({
          originalText: highlightContent,
          normalizedText: normalized,
          pageIndex,
          pageIndices,
          boundingBox,
          factIndex
        });
      }
    });
  }

  // Deduplicate while preserving the first occurrence's page info
  const dedupedHighlights: string[] = [];
  const dedupedPageIndices: (number | null)[] = [];
  const dedupedAllPageIndices: ((number[] | null) | null)[] = [];
  const highlightIndexMap: Map<string, number> = new Map();
  let dedupPreferredIndex: number | null = null;

  highlightEntries.forEach((entry: HighlightEntry) => {
    if (!entry.normalizedText || entry.normalizedText.length === 0) {
      return;
    }

    if (!highlightIndexMap.has(entry.normalizedText)) {
      const newIndex: number = dedupedHighlights.length;
      highlightIndexMap.set(entry.normalizedText, newIndex);
      dedupedHighlights.push(entry.normalizedText);
      dedupedPageIndices.push(entry.pageIndex ?? null);
      dedupedAllPageIndices.push(entry.pageIndices ?? null);
    }

    if (entry.factIndex === preferredFactIndex) {
      dedupPreferredIndex = highlightIndexMap.get(entry.normalizedText) ?? null;
    }
  });

  return {
    documentId,
    highlightTexts: dedupedHighlights,
    highlightPageIndices: dedupedPageIndices,
    highlightAllPageIndices: dedupedAllPageIndices,
    preferredFactIndex: dedupPreferredIndex,
    isTabular: isTabularFact(factSource),
    containsImageFacts,
    preferredFactSourceType
  };
}

/**
 * Build a document payload for the preview pane.
 *
 * If sourceDocument (from the document registry) is available, use it directly.
 * If not, fall back to minimal info from the fact's metadata and warn.
 */
export function buildDocumentPreviewPayload(
  sourceDocument: DocumentData | null = null,
  factSource: FactHighlightSource | null = null
): DocumentData | null {
  if (!sourceDocument && !factSource) {
    return null;
  }

  // Registry document available — backend already sends originalFilename,
  // documentName, and title consistently. Trust it as-is.
  if (sourceDocument) {
    const docId = sourceDocument.docId || sourceDocument.documentId;
    if (!docId) {
      console.warn('[buildDocumentPreviewPayload] Registry document has no docId');
      return null;
    }

    return { ...sourceDocument, docId };
  }

  // No registry document — build minimal payload from fact metadata
  if (factSource) {
    const docId = factSource.documentId || factSource.docId;
    if (!docId) return null;

    console.warn(`[buildDocumentPreviewPayload] No registry document for docId="${docId}". Using fact metadata only.`);

    const displayName = factSource.docDisplayLabel;
    return {
      docId,
      originalFilename: displayName,
      documentName: displayName,
      title: displayName,
    };
  }

  return null;
}

/**
 * Convert highlight request to fact highlights array for PdfFactViewer
 */
export function highlightRequestToFactHighlights(highlightRequest: HighlightRequest): FactHighlight[] {
  if (!highlightRequest || !Array.isArray(highlightRequest.highlightTexts)) {
    return [];
  }

  const pageIndices: (number | null)[] = Array.isArray(highlightRequest.highlightPageIndices)
    ? highlightRequest.highlightPageIndices
    : [];

  const allPageIndices: ((number[] | null) | null)[] = Array.isArray(highlightRequest.highlightAllPageIndices)
    ? highlightRequest.highlightAllPageIndices
    : [];

  return highlightRequest.highlightTexts.map((text: string, index: number): FactHighlight => {
    const pageIndex = (typeof pageIndices[index] === 'number' && Number.isFinite(pageIndices[index]))
      ? pageIndices[index] as number
      : null;

    return {
      id: `${highlightRequest.documentId}-${index}`,
      text,
      pageIndex,
      pageIndices: allPageIndices[index] ?? null
    };
  });
}

/**
 * Get the active highlight ID from a highlight request
 */
export function getActiveHighlightId(highlightRequest: HighlightRequest): string | null {
  if (!highlightRequest) {
    return null;
  }

  const factIndex: number | null = highlightRequest.preferredFactIndex;

  if (factIndex === null || factIndex === undefined) {
    if (highlightRequest.highlightTexts && highlightRequest.highlightTexts.length > 0) {
      return `${highlightRequest.documentId}-0`;
    }
    return null;
  }

  return `${highlightRequest.documentId}-${factIndex}`;
}
